// ============================================
// useGlobalSearch Hook
// ============================================
// Provides global search functionality with client-side caching

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/auth';
import { useTenantStore } from '../stores/tenant';
import { useSearchStore, type SearchEntityType, type SearchResult } from '../stores/search';

/**
 * Hook options
 */
interface UseGlobalSearchOptions {
  /** Entity types to include in search results */
  types?: SearchEntityType[];
  /** Maximum number of results to return */
  limit?: number;
  /** Debounce delay in milliseconds */
  debounceMs?: number;
  /** Whether to fetch search index immediately */
  enabled?: boolean;
}

/**
 * Hook return value
 */
interface UseGlobalSearchResult {
  /** Current search query */
  query: string;
  /** Set search query */
  setQuery: (query: string) => void;
  /** Search results */
  results: SearchResult[];
  /** Whether search is loading */
  isLoading: boolean;
  /** Whether the search index is being fetched */
  isIndexLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refresh the search index */
  refreshIndex: () => void;
  /** Total items in the index */
  totalItems: number;
  /** Search for a specific query (without updating state) */
  searchFor: (query: string) => SearchResult[];
}

/**
 * Global search hook with client-side Fuse.js
 *
 * Features:
 * - Fetches search index from API and caches for 5 minutes
 * - Uses Fuse.js for fuzzy search
 * - Supports filtering by entity type
 * - Debounced search input
 */
export function useGlobalSearch(options: UseGlobalSearchOptions = {}): UseGlobalSearchResult {
  const { types, limit = 10, debounceMs = 200, enabled = true } = options;
  const user = useAuthStore(state => state.user);
  const mode = useTenantStore(state => state.mode);
  const activeTenantId = useTenantStore(state => state.activeTenantId);

  const canQueryTenantScoped = useMemo(() => {
    if (!user) {
      return false;
    }

    const isAdmin = user.role === 'admin' || user.role === 'super_admin';
    if (isAdmin) {
      return mode !== 'all' && !!activeTenantId;
    }

    return true;
  }, [user, mode, activeTenantId]);

  // Local state for search query
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Get store actions and state
  const {
    setIndex,
    setLoading,
    setError,
    isCacheStale,
    search,
    items,
    isLoading: storeLoading,
    error: storeError,
  } = useSearchStore();

  // Debounce the query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [query, debounceMs]);

  // Fetch search index with React Query
  const {
    data,
    isLoading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['search-index'],
    queryFn: async () => {
      setLoading(true);
      try {
        const response = await api.getSearchIndex();
        return response;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to fetch search index';
        setError(message);
        throw error;
      }
    },
    enabled: enabled && canQueryTenantScoped && isCacheStale(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    refetchOnWindowFocus: false,
    retry: 2,
  });

  // Update store when data changes
  useEffect(() => {
    if (data?.items) {
      setIndex(data.items);
    }
  }, [data, setIndex]);

  // Perform search
  const results = useMemo(() => {
    return search(debouncedQuery, { types, limit });
  }, [debouncedQuery, types, limit, search]);

  // Search function for external use
  const searchFor = useCallback(
    (q: string): SearchResult[] => {
      return search(q, { types, limit });
    },
    [types, limit, search]
  );

  // Refresh function
  const refreshIndex = useCallback(() => {
    useSearchStore.getState().invalidateCache();
    refetch();
  }, [refetch]);

  return {
    query,
    setQuery,
    results,
    isLoading: storeLoading,
    isIndexLoading: queryLoading,
    error: storeError ?? (queryError instanceof Error ? queryError.message : null),
    refreshIndex,
    totalItems: items.length,
    searchFor,
  };
}

/**
 * Hook for prefetching the search index
 * Use this in a parent component to preload the index
 */
export function usePrefetchSearchIndex() {
  const user = useAuthStore(state => state.user);
  const mode = useTenantStore(state => state.mode);
  const activeTenantId = useTenantStore(state => state.activeTenantId);
  const isCacheStale = useSearchStore(state => state.isCacheStale);
  const setIndex = useSearchStore(state => state.setIndex);
  const setLoading = useSearchStore(state => state.setLoading);
  const setError = useSearchStore(state => state.setError);

  const canQueryTenantScoped =
    !!user &&
    (user.role === 'manager' ||
      user.role === 'user' ||
      ((user.role === 'admin' || user.role === 'super_admin') &&
        mode !== 'all' &&
        !!activeTenantId));

  useQuery({
    queryKey: ['search-index'],
    queryFn: async () => {
      setLoading(true);
      try {
        const response = await api.getSearchIndex();
        setIndex(response.items);
        return response;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to fetch search index';
        setError(message);
        throw error;
      }
    },
    enabled: canQueryTenantScoped && isCacheStale(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
